import { formatRupees } from './money.js';

export interface NoticePayload {
  [key: string]: unknown;
}

/** Where a notification leads. Anything unknown opens the list of that kind of work, never nowhere. */
export function noticeTarget(type: string, payload: NoticePayload = {}): string {
  const dealId = payload['dealId'];
  if ((type.startsWith('memo.') || type.startsWith('deal.')) && typeof dealId === 'string') {
    return `/deals/${dealId}`;
  }
  if (type.startsWith('visit.')) return '/visits';
  if (type.startsWith('renewal')) return '/renewals';
  if (type.startsWith('billing_') || type === 'trial_ending') return '/billing';
  return '/notifications';
}

/** The i18n key and values for a notification's sentence. */
export function noticeSentence(
  type: string,
  payload: NoticePayload,
): { key: string; values: Record<string, string | number> } {
  const text = (v: unknown): string => (typeof v === 'string' ? v : '');
  const num = (v: unknown): number => (typeof v === 'number' ? v : 0);
  /** A slot in words: the stored `call_me` is never shown. */
  const slot = (v: unknown): string => (v === 'call_me' ? 'call to fix a time' : text(v));
  const who = (v: unknown, party: unknown): string =>
    text(v) !== ''
      ? text(v)
      : party === 'owner'
        ? 'The owner'
        : party === 'tenant'
          ? 'The client'
          : 'Someone';
  switch (type) {
    case 'visit.requested':
      return {
        key: 'notice.visitRequested',
        values: { property: text(payload['property']), slot: slot(payload['slot']) },
      };
    case 'visit.assigned':
      return {
        key: 'notice.visitAssigned',
        values: { property: text(payload['property']), slot: slot(payload['slot']) },
      };
    case 'visit.client_cancelled':
      return {
        key: 'notice.visitClientCancelled',
        values: { property: text(payload['property']), slot: slot(payload['slot']) },
      };
    case 'visit.client_rescheduled':
      return {
        key: 'notice.visitClientRescheduled',
        values: { property: text(payload['property']), slot: slot(payload['slot']) },
      };
    case 'memo.opened':
      return {
        key: 'notice.memoOpened',
        values: {
          name: who(payload['name'], payload['party']),
          property: text(payload['property']),
        },
      };
    case 'memo.signed':
      return {
        key: 'notice.memoSigned',
        values: {
          name: who(payload['name'], payload['party']),
          property: text(payload['property']),
          signed: num(payload['signed']),
          total: num(payload['total']),
        },
      };
    case 'memo.fully_signed':
      return { key: 'notice.memoFullySigned', values: { property: text(payload['property']) } };
    case 'deal.token_recorded':
      return {
        key: 'notice.tokenRecorded',
        values: {
          property: text(payload['property']),
          amount: formatRupees(num(payload['amountPaise'])),
        },
      };
    case 'deal.receipt_recorded':
      return {
        key: 'notice.receiptRecorded',
        values: {
          property: text(payload['property']),
          amount: formatRupees(num(payload['amountPaise'])),
        },
      };
    case 'deal.token_claimed':
      return { key: 'notice.tokenClaimed', values: { property: text(payload['property']) } };
    case 'deal.token_confirmed':
      return { key: 'notice.tokenConfirmed', values: { property: text(payload['property']) } };
    case 'deal.token_not_received':
      return { key: 'notice.tokenNotReceived', values: { property: text(payload['property']) } };
    case 'renewal_due':
      return { key: 'notice.renewalDue', values: { days: num(payload['daysUntilDue']) } };
    case 'renewal_started':
      return { key: 'notice.renewalStarted', values: {} };
    case 'trial_ending':
      return { key: 'notice.trialEnding', values: { count: num(payload['daysLeft']) } };
    case 'billing_paid':
      return { key: 'notice.billingPaid', values: {} };
    case 'billing_failed':
      return { key: 'notice.billingFailed', values: {} };
    case 'billing_cancelled':
      return { key: 'notice.billingCancelled', values: {} };
    case 'billing_status':
      return { key: 'notice.billingStatus', values: { status: text(payload['status']) } };
    default:
      return { key: 'notice.generic', values: {} };
  }
}
